import type { Middleware, MiddlewareCallbackParams } from 'openapi-fetch'

/**
 * The one place a credential is attached to an outgoing request.
 *
 * `onRequest` is declared over `Pick<…, 'request'>` rather than the full
 * callback params for two reasons: it is honest about the only field this
 * middleware reads, and it lets a test call it with `{ request }` instead of
 * fabricating a `MergedOptions` it does not use. A narrower parameter is still
 * assignable to openapi-fetch's `MiddlewareOnRequest`, so the intersection with
 * `Middleware` keeps `api.use()` type-checked.
 *
 * The non-optional `onRequest` matters too: openapi-fetch's `Middleware` is a
 * union in which each hook is optional, so a caller would need `mw.onRequest!`
 * — a non-null assertion to quiet the compiler, which CLAUDE.md rules out.
 */
export type AuthMiddleware = Middleware & {
  onRequest: (params: Pick<MiddlewareCallbackParams, 'request'>) => Promise<Request>
}

export function createAuthMiddleware(getToken: () => Promise<string | null>): AuthMiddleware {
  return {
    async onRequest({ request }) {
      // Called per request, not once at construction: Firebase ID tokens
      // expire hourly, and a cached one turns into a 401 an hour into a session.
      const token = await getToken()
      if (token) request.headers.set('Authorization', `Bearer ${token}`)
      return request
    },
  }
}
